/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2026
///                                    RXO Inc.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of RXO Inc. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import AwardImportModal from "../award-import/AwardImportModal";
import {
  fetchBidMessages,
  closeModeSpecificBid,
  closeBidWon,
  resetCloseBidModeActionState,
  resetCloseBidWonActionState,
  fetchBidSummary
} from "../../../slices/bidSummarySlice";
import { userRole } from "../../../helpers/types";
import CloseBidModal from "../award-import/CloseBidModal";
import messages from "../../../helpers/messages";
import { isEffectiveCoyoteBid } from "../../../helpers/isEffectiveCoyoteBid";

function ImportAwardState({ bidId, user, isAwardImportComplete, summary }) {
  const dispatch = useDispatch();
  const {
    closeBidModeActionIsLoading,
    closeBidModeActionFailure,
    closeBidWonActionIsLoading,
    closeBidWonActionFailure
  } = useSelector(state => state.bidSummary);
  const { enableCloseBidWon, selfPricedCloseWon } = useSelector(
    state => state.featureFlags
  );
  const route = useLocation().pathname.split("/")[3] || "";
  const [openAwardImportModal, setOpenAwardImportModal] = useState(false);
  const [openCloseBidModal, setOpenCloseBidModal] = useState(false);

  const { role } = user;
  const effectiveCoyoteBid = isEffectiveCoyoteBid(summary);
  const isManagementUser = role === userRole.management;
  const isSelfPriced = summary?.modes?.some(m => m.bidOwnerRepType === 1 && m.mode === 1);
  const shouldShowCloseBidButton = enableCloseBidWon 
    ? (effectiveCoyoteBid || isManagementUser || isSelfPriced)
    : true;
  const text = isAwardImportComplete
    ? messages.awardImportCompleteMessage.defaultMessage
    : messages.awardImportIncompleteMessage.defaultMessage;

  const handleAwardImportModalClose = imported => {
    if (imported) {
      route === "summary" && dispatch(fetchBidMessages(bidId));
    }
    setOpenAwardImportModal(false);
  };

  const handleCloseBidClick = () => {
    dispatch(closeModeSpecificBid(bidId, role, route));
  };

  const handleCloseWonClick = () => {
    if (enableCloseBidWon) {
      dispatch(closeBidWon(bidId, role, route));
    }
  };

  const handleClose = fetchDetails => {
    if (fetchDetails) {
      dispatch(resetCloseBidModeActionState());
      if (enableCloseBidWon) {
        dispatch(resetCloseBidWonActionState());
      }
      dispatch(fetchBidSummary({ bidId, role }));
      route === "summary" && dispatch(fetchBidMessages(bidId));
    }
    setOpenCloseBidModal(false);
  };

  return (
    <>
      <p className="inline-block font-size-14 pt-16 mb-0">{text}</p>
      <div className="fr py-8 my-2">
        <button
          className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 h-32 px-12 short-button"
          data-testid="import-award"
          onClick={() => setOpenAwardImportModal(true)}
        >
          {messages.importAward.defaultMessage}
        </button>
        {isAwardImportComplete && shouldShowCloseBidButton && (
          <button
            className="text-button univers-67-bold-cond font-size-14 uppercase mx-8 h-32"
            data-testid="close-bid"
            style={{ minWidth: "85px" }}
            onClick={() => setOpenCloseBidModal(true)}
          >
            {messages.closeBid.defaultMessage}
          </button>
        )}
      </div>
      {openAwardImportModal && (
        <AwardImportModal
          bidId={bidId}
          user={user}
          handleAwardImportModalClose={handleAwardImportModalClose}
        />
      )}
      {openCloseBidModal && (
        <CloseBidModal
          handleClose={handleClose}
          handleCloseBidClick={handleCloseBidClick}
          handleCloseWonClick={
            enableCloseBidWon && (!isSelfPriced || selfPricedCloseWon)
              ? handleCloseWonClick
              : undefined
          }
          isLoading={closeBidModeActionIsLoading || (enableCloseBidWon && closeBidWonActionIsLoading)}
          isFailure={closeBidModeActionFailure || (enableCloseBidWon && closeBidWonActionFailure)}
          summary={summary}
          enableCloseLostCrmForm={false}
        />
      )}
    </>
  );
}

export default ImportAwardState;
